import React from 'react';
import { Archive, User, ShieldAlert, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

export const SEVERITY_STYLES = {
  BREACH: 'bg-red-500/20 text-red-400 border-red-500/30',
  BEYOND_LAW: 'bg-rose-600/20 text-rose-300 border-rose-500/30',
  WARNING: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

export const REGIME_STYLES = {
  FRHV: 'bg-blue-500/15 text-blue-400',
  BUSINESS: 'bg-teal-500/15 text-teal-400',
  UNKNOWN: 'bg-slate-500/15 text-slate-400',
};

export const formatTS = (ts) => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ts; }
};

export const formatDateShort = (ts) => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ts; }
};

export const StatCard = ({ label, value, icon: Icon, color, loading }) => {
  const bg = {
    emerald: 'bg-emerald-500/10 border-emerald-500/20',
    blue: 'bg-blue-500/10 border-blue-500/20',
    red: 'bg-red-500/10 border-red-500/20',
    amber: 'bg-amber-500/10 border-amber-500/20',
  }[color] || 'bg-slate-700/50 border-slate-600';

  const iconColor = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
  }[color] || 'text-slate-400';

  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={`stat-${color}`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />
        <div className="min-w-0">
          <p className="text-xs text-slate-400 truncate">{label}</p>
          <p className="text-lg font-bold text-white">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin text-slate-500 inline" /> : value}
          </p>
        </div>
      </div>
    </div>
  );
};

export const SegmentsTable = ({ segments, total, page, totalPages, loading, onPageChange }) => (
  <div className="bg-slate-800/50 rounded-2xl border border-slate-700" data-testid="segments-table-wrapper">
    <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
      <h3 className="text-white font-medium text-sm">
        Archived Segments
        <span className="text-slate-400 font-normal ml-2">({total} total)</span>
      </h3>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    ) : segments.length === 0 ? (
      <div className="text-center py-16 text-slate-500">
        <Archive className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No archived segments found</p>
        <p className="text-xs mt-1">Archives appear after the daily 03:00 AEST run processes segments older than 47 days</p>
      </div>
    ) : (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="px-4 py-3 font-medium">Driver</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Start</th>
                <th className="px-4 py-3 font-medium">End</th>
                <th className="px-4 py-3 font-medium">Duration</th>
                <th className="px-4 py-3 font-medium">Regime</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Archived</th>
              </tr>
            </thead>
            <tbody>
              {segments.map((seg) => (
                <tr key={seg.archive_id} className="border-b border-slate-700/40 hover:bg-slate-700/20 transition">
                  <td className="px-4 py-3 text-white flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <span className="truncate max-w-[140px]">{seg.driver_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      seg.segment_type === 'WORK'
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-sky-500/20 text-sky-400'
                    }`}>
                      {seg.segment_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatTS(seg.start_ts)}</td>
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatTS(seg.end_ts)}</td>
                  <td className="px-4 py-3 text-white font-mono text-xs">{seg.duration_display || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${REGIME_STYLES[seg.regime] || REGIME_STYLES.UNKNOWN}`}>
                      {seg.regime || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs hidden lg:table-cell whitespace-nowrap">
                    {formatDateShort(seg.archived_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </>
    )}
  </div>
);

export const BreachesTable = ({ breaches, total, page, totalPages, loading, onPageChange }) => (
  <div className="bg-slate-800/50 rounded-2xl border border-slate-700" data-testid="breaches-table-wrapper">
    <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
      <h3 className="text-white font-medium text-sm">
        Breach Evidence Register
        <span className="text-slate-400 font-normal ml-2">({total} total)</span>
      </h3>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 text-red-400 animate-spin" />
      </div>
    ) : breaches.length === 0 ? (
      <div className="text-center py-16 text-slate-500">
        <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No breach evidence recorded</p>
        <p className="text-xs mt-1">Breaches are detected during the archive process for WORK segments</p>
      </div>
    ) : (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="px-4 py-3 font-medium">Driver</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Severity</th>
                <th className="px-4 py-3 font-medium">Detected</th>
                <th className="px-4 py-3 font-medium">Threshold</th>
                <th className="px-4 py-3 font-medium">Actual</th>
                <th className="px-4 py-3 font-medium">Regime</th>
              </tr>
            </thead>
            <tbody>
              {breaches.map((br) => (
                <tr key={br.breach_id} className="border-b border-slate-700/40 hover:bg-slate-700/20 transition">
                  <td className="px-4 py-3 text-white flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <span className="truncate max-w-[140px]">{br.driver_name}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">
                    {(br.breach_type || '').replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                      SEVERITY_STYLES[br.severity] || 'bg-slate-600/20 text-slate-400 border-slate-500/30'
                    }`}>
                      {br.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatTS(br.detected_at)}</td>
                  <td className="px-4 py-3 text-white font-mono text-xs">{br.threshold_display || '-'}</td>
                  <td className="px-4 py-3 text-red-400 font-mono text-xs">{br.actual_display || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${REGIME_STYLES[br.regime] || REGIME_STYLES.UNKNOWN}`}>
                      {br.regime || 'N/A'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </>
    )}
  </div>
);

export const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700" data-testid="archive-pagination">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid="prev-page-btn"
      >
        <ChevronLeft className="w-4 h-4" /> Prev
      </button>
      <span className="text-xs text-slate-500">
        Page {page} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid="next-page-btn"
      >
        Next <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};
